/**
 * Iter116 — Stammdaten-Vervollständigungs-Hinweis fürs Cockpit
 *
 * Zeigt gelbe Warning-Cards für Firmen mit fehlenden Pflicht-Stammdaten und
 * blasse Info-Cards für Firmen mit fehlenden empfohlenen Feldern.
 * Klick auf „Jetzt ergänzen" → wechselt zur Firma + öffnet Einstellungen.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Info, ArrowRight } from "lucide-react";

export default function StammdatenChecklistCard() {
  const navigate = useNavigate();
  const { setCompany } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("stammdaten-dismissed") || "[]");
    } catch {
      return [];
    }
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/my/stammdaten-status");
      setData(res.data);
    } catch (e) {
      // Silent fail — nicht-kritisches Feature
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleGoToSettings = async (item) => {
    try {
      const companies = await apiClient.get("/my/companies");
      const co = (companies.data || []).find((c) => c._id === item.company_id);
      if (co) {
        setCompany(co);
        localStorage.setItem("demoCompanyId", co._id);
      }
    } catch {
      // ignore
    }
    navigate("/settings");
  };

  const handleDismiss = (companyId) => {
    const next = [...dismissed, companyId];
    setDismissed(next);
    localStorage.setItem("stammdaten-dismissed", JSON.stringify(next));
  };

  if (!data || data.incomplete_count === 0) return null;

  // Filter dismissed
  const visible = data.incomplete.filter((i) => !dismissed.includes(i.company_id));
  if (visible.length === 0) return null;

  const warnings = visible.filter((i) => i.severity === "warning");
  const infos = visible.filter((i) => i.severity === "info");

  return (
    <Card className="border border-amber-200 bg-amber-50/50 p-4" data-testid="stammdaten-checklist-card">
      <div className="flex items-start gap-2 mb-3">
        <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-amber-900">
            Stammdaten unvollständig ({visible.length})
          </h3>
          <p className="text-xs text-amber-800">
            Fehlende Pflichtfelder verhindern u.a. IBAN-Auto-Routing von Bank-Statements, Swiss QR-Rechnungen und PostFinance-camt-Import.
          </p>
        </div>
      </div>

      <ul className="space-y-2" data-testid="stammdaten-checklist-list">
        {warnings.map((item) => (
          <li
            key={item.company_id}
            className="bg-white border border-amber-300 rounded p-3 flex items-center justify-between gap-3"
            data-testid={`stammdaten-row-${item.company_id}`}
          >
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900">{item.company_name}</div>
              <div className="text-xs text-amber-800 mt-0.5">
                Pflicht fehlt: <strong>{item.missing_required.join(", ")}</strong>
                {item.missing_recommended.length > 0 && (
                  <span className="text-slate-500"> · zusätzlich empfohlen: {item.missing_recommended.join(", ")}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="sm"
                onClick={() => handleGoToSettings(item)}
                className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                data-testid={`stammdaten-edit-${item.company_id}`}
              >
                Jetzt ergänzen <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDismiss(item.company_id)}
                data-testid={`stammdaten-dismiss-${item.company_id}`}
                title="Vorerst ausblenden"
                className="text-amber-700"
              >
                ×
              </Button>
            </div>
          </li>
        ))}

        {infos.map((item) => (
          <li
            key={item.company_id}
            className="bg-white border border-slate-200 rounded p-3 flex items-center justify-between gap-3"
            data-testid={`stammdaten-row-${item.company_id}`}
          >
            <div className="min-w-0 flex-1 flex items-center gap-2">
              <Info className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-700">{item.company_name}</div>
                <div className="text-xs text-slate-500">
                  Empfohlen: <strong>{item.missing_recommended.join(", ")}</strong>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleGoToSettings(item)}
                data-testid={`stammdaten-edit-${item.company_id}`}
              >
                Ergänzen
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDismiss(item.company_id)}
                data-testid={`stammdaten-dismiss-${item.company_id}`}
                title="Vorerst ausblenden"
                className="text-slate-400"
              >
                ×
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
